import React, { useState, useEffect } from "react";
import Select from "react-select";
import { getAllServices, getOneEnquiry, updateEnquiry } from "../../../redux/actions/authActions"; 
import { useDispatch, useSelector } from "react-redux";
import { useParams, useNavigate } from "react-router-dom";
import { message, Spin } from "antd";

function ViewEnquires() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { id } = useParams();

  const services = useSelector((state) => state.auth.allServices);
  const [loading, setLoading] = useState(true);
  const [isEditing, setIsEditing] = useState(false);

  // This state holds the current values shown in the inputs
  const [formData, setFormData] = useState({
    companyName: "",
    clientName: "",
    emailAddress: "",
    clientType: "Private Limited",
    phoneNumber: "",
    opted: [],
  });

  // NEW: This state stores the original data from the server as a backup
  const [originalData, setOriginalData] = useState(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        await dispatch(getAllServices());
        const res = await dispatch(getOneEnquiry(id));
        const data = res?.data || res;

        if (data) {
          const formattedData = {
            companyName: data.companyName || "",
            clientName: data.clientName || "",
            emailAddress: data.emailAddress || "",
            clientType: data.clientType || "Private Limited",
            phoneNumber: data.phoneNumber || "",
            opted: data.services ? data.services.map(s => s._id || s) : [],
          };
          setFormData(formattedData);
          setOriginalData(formattedData); // Store the backup here
        }
        setLoading(false);
      } catch (error) {
        message.error("Failed to load enquiry details");
        setLoading(false);
      }
    };
    fetchData();
  }, [dispatch, id]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "phoneNumber") {
      const numericValue = value.replace(/\D/g, "");
      if (numericValue.length <= 10) setFormData(prev => ({ ...prev, [name]: numericValue }));
    } else if (name === "clientName") {
      const lettersOnly = value.replace(/[^a-zA-Z\s]/g, "");
      if (lettersOnly.length <= 40) setFormData(prev => ({ ...prev, [name]: lettersOnly }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  // UPDATED: Cancel logic now restores the original data
  const handleCancel = () => {
    setFormData(originalData); // Reset fields to the original values
    setIsEditing(false);
  };

  const handleSave = async () => {
    if (formData.phoneNumber.length < 10) return message.error("Invalid Phone Number");
    if (formData.opted.length === 0) return message.error("Please select at least one service");

    try {
      await dispatch(updateEnquiry(id, formData)); 
      message.success("Enquiry updated successfully");
      setOriginalData(formData); // Update the backup to the newly saved data
      setIsEditing(false);
    } catch (error) {
      message.error("Update failed");
    }
  };

  const serviceOptions = services?.map((service) => ({
    value: service._id,
    label: service.name,
  }));

  if (loading) return <div className="flex justify-center items-center h-screen"><Spin size="large" /></div>;

  return (
    <div className="container mx-auto px-4 py-8">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h1 className="text-3xl font-bold text-gray-800">Enquiry Details</h1>
          <p className="text-sm text-gray-500 mt-1">
            {isEditing ? "Editing: " : "Viewing: "} {formData.companyName}
          </p>
        </div>
        <div className="flex gap-3">
          <button 
            onClick={isEditing ? handleCancel : () => setIsEditing(true)}
            className={`px-6 py-2 rounded-full text-sm font-medium transition-colors ${
              isEditing ? "bg-red-500 text-white" : "bg-blue-600 text-white"
            }`}
          >
            {isEditing ? "Cancel" : "Edit Enquiry"}
          </button>
        </div>
      </div>

      <div className="bg-white rounded-xl shadow-lg p-8 border border-gray-100">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-4">
            <div>
              <label className="block mb-2 text-xs font-semibold text-gray-500 uppercase">Company Name</label>
              <input
                name="companyName"
                type="text"
                value={formData.companyName}
                onChange={handleInputChange}
                className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                disabled={!isEditing}
              />
            </div>
            <div>
              <label className="block mb-2 text-xs font-semibold text-gray-500 uppercase">Contact Person</label>
              <input
                name="clientName"
                type="text"
                value={formData.clientName}
                onChange={handleInputChange}
                className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                disabled={!isEditing}
              />
            </div>
            <div>
              <label className="block mb-2 text-xs font-semibold text-gray-500 uppercase">Email Address</label>
              <input
                name="emailAddress"
                type="email"
                value={formData.emailAddress}
                onChange={handleInputChange}
                className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                disabled={!isEditing}
              />
            </div>
          </div>

          <div className="space-y-4">
            <div>
              <label className="block mb-2 text-xs font-semibold text-gray-500 uppercase">Client Type</label>
              <select
                name="clientType"
                value={formData.clientType}
                onChange={handleInputChange}
                disabled={!isEditing}
                className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-lg block w-full p-2.5 appearance-none"
                style={{ 
                  backgroundImage: `url("data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 24 24' stroke='%236b7280'%3E%3Cpath stroke-linecap='round' stroke-linejoin='round' stroke-width='2' d='M19 9l-7 7-7-7'%3E%3C/path%3E%3C/svg%3E")`,
                  backgroundRepeat: 'no-repeat',
                  backgroundPosition: 'right 0.75rem center',
                  backgroundSize: '1.5em 1.5em',
                  paddingRight: '2.5rem'
                }}
              >
                <option value="Private Limited">Private Limited</option>
                <option value="Partnership">Partnership</option>
                <option value="Proprietorship">Proprietorship</option>
              </select>
            </div>
            <div>
              <label className="block mb-2 text-xs font-semibold text-gray-500 uppercase">Phone Number</label>
              <input
                name="phoneNumber"
                type="text"
                value={formData.phoneNumber}
                onChange={handleInputChange}
                className="bg-gray-50 border border-gray-200 text-gray-900 text-sm rounded-lg block w-full p-2.5"
                disabled={!isEditing}
              />
            </div>
            <div>
              <label className="block mb-2 text-xs font-semibold text-gray-500 uppercase">Services Enquired</label>
              <Select
                isMulti
                value={serviceOptions?.filter(option => formData.opted.includes(option.value))}
                options={serviceOptions}
                isDisabled={!isEditing}
                onChange={(selected) => setFormData(prev => ({ ...prev, opted: selected ? selected.map(s => s.value) : [] }))}
                className="text-sm"
              />
            </div>
          </div>
        </div>

        <div className="mt-8 pt-6 border-t border-gray-100 flex justify-end gap-4">
          {isEditing ? (
            <button onClick={handleSave} className="px-8 py-2.5 bg-green-600 text-white rounded-full hover:bg-green-700 shadow-md transition-all">
              Save Changes
            </button>
          ) : (
            <button onClick={() => navigate("/enquires")} className="px-8 py-2.5 bg-gray-800 text-white rounded-full hover:bg-black shadow-md transition-all">
              Back to List
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

export default ViewEnquires;